import classNames from "classnames"

import { FilterPanelItem } from "./index"

import Accordion from "@components/elements/V2/Accordion"
import PillButton from "@components/elements/V2/Buttons/PillButton"
import Radio from "@components/elements/V2/Buttons/Radio"
import TextButton from "@components/elements/V2/Buttons/TextButton"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"
import { Checkbox } from "@components/elements/V2/Inputs/Checkbox"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"
import React, { useMemo } from "react"

type SidebarProps = {
  isOpen: boolean
  filters?: FilterPanelItem[]
  activeFilters: string[]
  onFilterChange: (
    tag: string,
    checked: boolean,
    group: FilterPanelItem
  ) => void
  onClearAllFilters?: () => void
  subFilterTitle?: string
  subFilterDescription?: string
  subFilterCtaText?: string
  subFilterCtaLink?: string | YocoCom.YocoLink
  sortByMobileOrder?: boolean
}

const ProductCatalogueSidebar = ({
  isOpen,
  filters = [],
  activeFilters,
  onFilterChange,
  onClearAllFilters,
  subFilterTitle,
  subFilterDescription,
  subFilterCtaText,
  subFilterCtaLink,
  sortByMobileOrder = false,
}: SidebarProps) => {
  const sortedFilters = useMemo(() => {
    if (!sortByMobileOrder) return filters

    return [...filters].sort(
      (firstFilter, secondFilter) =>
        firstFilter.mobileOrder - secondFilter.mobileOrder
    )
  }, [filters, sortByMobileOrder])

  return (
    <div
      className={classNames(
        "relative h-full min-h-[400px] shrink-0 overflow-x-hidden transition-all duration-300 lg:min-h-[500px]",
        isOpen ? "w-full lg:w-[280px]" : "hidden w-0 lg:block lg:w-0"
      )}
    >
      <div
        className={classNames(
          "h-full w-full bg-white px-2 pb-24 pt-2 transition-transform duration-300 ease-in-out",
          {
            "translate-x-0": isOpen,
            "-translate-x-full": !isOpen,
          }
        )}
      >
        <div className="h-full overflow-y-auto pr-2">
          <div className="flex h-fit w-full flex-shrink-0 flex-col gap-[10px] overflow-y-auto pb-[10px] pl-[10px] pr-[10px] lg:w-[251px]">
            <div className="pb-1">
              <Typography
                font="grotesk"
                color={Color.Black}
                text="Filter Products"
                size="lead-md-fixed"
                weight="medium"
                className="tracking-tight"
              />
            </div>

            <div className="flex flex-col gap-[10px]">
              {sortedFilters.map((block, index) => {
                const tagsList = block.tags || []

                return (
                  <Accordion
                    key={block._uid}
                    title={block.title}
                    textColor={Color.Black}
                    iconColor={Color.Black}
                    icon="arrow"
                    openByDefault={index === 0}
                    contentTopPadding={true}
                    titleSize="body-lg"
                    titleFontWeight="book"
                    titleClassName="hover:text-blue"
                    className="w-full border-b border-gray pb-5"
                  >
                    <ul className="m-0 mt-4 list-none space-y-5 p-0">
                      {tagsList.map((tag, tagIndex) => {
                        const uniqueId = `${block._uid}-${tagIndex}`
                        const isChecked = activeFilters.includes(tag)

                        return (
                          <li key={uniqueId}>
                            {block.selectionMode === "single" ? (
                              <div
                                className="transition-all"
                                onClick={() =>
                                  isChecked && onFilterChange(tag, false, block)
                                }
                              >
                                <Radio
                                  id={uniqueId}
                                  name={block._uid}
                                  checked={isChecked}
                                  onChange={() =>
                                    onFilterChange(tag, true, block)
                                  }
                                  size="md"
                                  label={tag}
                                />
                              </div>
                            ) : (
                              <Checkbox
                                id={uniqueId}
                                label={tag}
                                isChecked={isChecked}
                                onChange={(e) =>
                                  onFilterChange(tag, e.target.checked, block)
                                }
                                className={classNames(
                                  "transition-all",
                                  isChecked &&
                                    "[&_div.h-20]:ring-1 [&_div.h-20]:ring-blue/30 [&_div.h-20]:ring-offset-1"
                                )}
                              />
                            )}
                          </li>
                        )
                      })}
                    </ul>
                  </Accordion>
                )
              })}
            </div>

            {onClearAllFilters && (
              <div className="pl-[2px]">
                <TextButton
                  style="underlined"
                  color={Color.Blue}
                  weight="book"
                  text="Clear All"
                  onClick={onClearAllFilters}
                  textSize="body-sm-fixed"
                />
              </div>
            )}

            {subFilterTitle && (
              <div className="mt-4 border-gray pl-[2px] pt-5">
                <Typography
                  font="grotesk"
                  color={Color.Black}
                  text={subFilterTitle}
                  size="body-md"
                  weight="medium"
                />
                {subFilterDescription && (
                  <Typography
                    font="grotesk"
                    color={Color.Black}
                    text={subFilterDescription}
                    size="body-sm"
                    weight="book"
                    className="mt-1"
                  />
                )}
                {subFilterCtaText && subFilterCtaLink && (
                  <>
                    <TextButton
                      style="underlined"
                      color={Color.Blue}
                      weight="medium"
                      text={subFilterCtaText}
                      linkUrl={subFilterCtaLink}
                      textSize="body-sm-fixed"
                      className="mt-3 lg:hidden"
                    />
                    <PillButton
                      style="solid"
                      size="medium"
                      color={Color.Blue}
                      text={subFilterCtaText}
                      linkUrl={subFilterCtaLink}
                      fontSize="body-sm-fixed"
                      icon={<ArrowIcon className="!h-[14px] !w-[14px]" />}
                      className="mt-3 hidden lg:flex"
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductCatalogueSidebar
